<template>
    <div>
        <!-- Modal for selecting booking details -->
        <Modal v-model="showModal">
            <template #title> {{ trans('online_booking.quick_booking.find_time') }} </template>

            <div class="space-y-6">
                <!-- Calendar selection -->
                <div class="flex">
                    <div class="col-xs-12">
                        <div class="form-group" :class="{ 'has-error': errors.has('calendarid') }">
                            <label for="calendar" class="control-label">
                                {{ trans('online_booking.quick_booking.select_calendar') }}
                                <!-- Show calendar color if available -->
                                <i
                                    v-if="chosen_calendar && chosen_calendar.color"
                                    class="fa-solid fa-circle ml-3"
                                    :style="'color:' + chosen_calendar.color"
                                ></i>
                            </label>

                            <select
                                v-model="booking.calendarid"
                                class="form-control"
                                :id="`calendar-select-${booking.calendarid || 'default'}`"
                            >
                                <option value="">{{ trans('online_booking.quick_booking.all_calendars') }}</option>
                                <option v-for="calendar in filteredCalendars" :key="calendar.id" :value="calendar.id">
                                    {{ calendar.name }}
                                </option>
                            </select>

                            <!-- Show validation error if calendar is required -->
                            <span v-show="errors.has('calendarid')" class="help-block">
                                {{ errors.first('calendarid') }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Practitioner selection -->
                <div class="flex" :class="{ 'has-error': errors.has('practitionerid') }">
                    <div class="col-xs-12">
                        <label for="calendar" class="control-label"> {{ trans('online_booking.quick_booking.select_practitioner') }} </label>

                        <select
                            v-model="booking.practitionerid"
                            class="form-control"
                            name="practitionerid"
                            id="booking-practitioners"
                        >
                            <option value="">{{ trans('online_booking.quick_booking.all_practitioners') }}</option>
                            <option v-for="practitioner in practitioners" :key="practitioner.id" :value="practitioner.id">
                                {{ practitioner.realname }}
                            </option>
                        </select>

                        <!-- Show validation error if required -->
                        <span v-show="errors.has('practitionerid')" class="help-block">
                        {{ errors.first('practitionerid') }}
                        </span>
                    </div>
                </div>

                <br>

                <!-- Product selection -->
                <div class="flex">
                    <div class="col-xs-12">
                        <div class="form-group" :class="{ 'has-error': errors.has('products') }">
                            <label for="product" class="control-label">
                                {{ trans('calendar.create_client_label_choose_product') }}
                                <i
                                    v-for="booking_product in booking.booking_products"
                                    class="zmdi zmdi-circle m-l-5"
                                    :style="[
                                        booking_product.product
                                            ? booking_product.product.color
                                                ? { color: booking_product.product.color }
                                                : { display: 'none' }
                                            : { display: 'none' },
                                    ]"
                                ></i>
                                <!-- Lock icon if a voucher is applied -->
                                <i class="zmdi zmdi-lock m-l-5" v-if="booking.voucher_clip.voucher_id > 0"></i>
                            </label>

                            <!-- Product selection component -->
                            <product-selector
                                :grouped-products="available_product_groups"
                                v-model="booking.booking_products"
                                :client-vouchers="available_client_vouchers"
                                :multiple="selectedPaymentGuarantee === null"
                                :allow-addons="is_addon_services_enabled && selectedPaymentGuarantee === null"
                                :edit-mode="is_edit_state"
                                :show-sort-icon="false"
                            ></product-selector>

                            <!-- Show validation error if required -->
                            <span v-show="errors.has('products')" class="help-block">
                                {{ errors.first('products') }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Preferred days of the week -->
                <div class="flex">
                    <div class="col-xs-12">
                        <div class="flex justify-between items-center mb-2">
                            <label for="preferred_day_week" class="control-label"> {{ trans('online_booking.quick_booking.preferred_day_week') }} </label>

                            <!-- Toggle all days selection -->
                            <a href="#" class="text-blue-500 hover:underline" @click.prevent="toggleAllDays">
                                {{ isAllDaysSelected ? trans('calendar.deselect_all') :  trans('calendar.select_all') }}
                            </a>
                        </div>

                        <!-- Checkboxes for selecting preferred days -->
                        <div class="checkbox flex gap-2 flex-wrap pl-2">
                            <div v-for="(day, index) in daysOfWeek" :key="day.code" class="inline-flex space-x-8">
                                <input
                                    v-model="booking.preferredDays"
                                    type="checkbox"
                                    :value="day.code"
                                    :id="`day-checkbox-${index}`"
                                    class="w-2 h-2"
                                />
                                <label :for="`day-checkbox-${index}`">{{ day.label }}</label>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Preferred times of the day -->
                <div class="flex">
                    <div class="col-xs-12">
                        <div class="flex justify-between items-center mb-2">
                            <label for="preferred_time_day" class="control-label"> {{ trans('online_booking.quick_booking.preferred_time_day') }}</label>
                            <!-- Checkboxes for selecting preferred times -->
                            <a href="#" class="text-blue-500 hover:underline" @click.prevent="toggleAllTimes">
                                {{ isAllTimesSelected ? trans('calendar.deselect_all') :  trans('calendar.select_all') }}
                            </a>
                        </div>

                        <!-- Checkboxes for selecting preferred times -->
                        <div class="checkbox flex gap-2 flex-wrap pl-2">
                            <div v-for="(time, index) in timesOfDay" :key="time.code" class="inline-flex space-x-8">
                                <input
                                    v-model="booking.preferredTimes"
                                    type="checkbox"
                                    :value="time.code"
                                    :id="`checkbox-${index}`"
                                    class="w-2 h-2"
                                />
                                <label :for="`checkbox-${index}`">{{ time.label }}</label> <!-- Use time.label for translation -->
                            </div>
                        </div>
                    </div>

                </div>

                <!-- Suggested appointment slots -->
                <div class="flex">
                    <div class="col-xs-12">

                            <label for="calendar" class="control-label"> {{ trans('online_booking.quick_booking.suggested_appointments') }} </label>


                        <!-- Component that suggests available slots -->
                        <SuggestedAppointments
                            v-bind="{
                            preferredDays: booking.preferredDays,
                            preferredTimes: booking.preferredTimes,
                            products: productIds,
                            calendarid: booking.calendarid,
                        }"
                            @slotSelected="handleSlotSelection"
                        />
                    </div>

                </div>
            </div>
        </Modal>
    </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useCalendarSidebarStore, SidebarViews } from 'Components/calendar/calendar_sidebar/store/calendarSidebarStore';
import Modal from '@/system/components/global/Modal.vue';
import ProductSelector from 'Components/global/ProductSelector.vue';
import cloneDeep from 'lodash/cloneDeep';
import forEach from 'lodash/forEach';
import sortBy from 'lodash/sortBy';
import Apps from '@/core/Apps';
import findIndex from 'lodash/findIndex';
import SuggestedAppointments from 'Components/calendar/SuggestedAppointments.vue';

export default {
    name: 'QuickBooking',
    components: {
        ProductSelector,
        Modal,
        SuggestedAppointments,
    },

    props: {
        copiedBooking: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            showModal: false,

            // Days of the week options
            daysOfWeek: [
                { code: 'mon', label: trans('online_booking.quick_booking.mon') },
                { code: 'tue', label: trans('online_booking.quick_booking.tue') },
                { code: 'wed', label: trans('online_booking.quick_booking.wed') },
                { code: 'thu', label: trans('online_booking.quick_booking.thu') },
                { code: 'fri', label: trans('online_booking.quick_booking.fri') },
                { code: 'sat', label: trans('online_booking.quick_booking.sat') },
                { code: 'sun', label: trans('online_booking.quick_booking.sun') },
            ],

            // Time of day options
            timesOfDay: [
                { code: 'morning', label: trans('online_booking.quick_booking.morning') },
                { code: 'afternoon', label: trans('online_booking.quick_booking.afternoon') },
                { code: 'evening', label: trans('online_booking.quick_booking.evening') },
            ],

            // Stores user booking details
            booking: {
                calendarid: '', // Selected calendar ID
                booking_products: [], // Selected products (including add-ons)
                products: [], // Additional products (if needed)
                voucher_clip: {}, // Applied voucher details
                practitionerid: '', // Selected practitioner
                preferredDays: [], // User's preferred days
                preferredTimes: [], // User's preferred time slots
                clientid: '', // Associated client ID
                client: [], // Client details
            },

            // Stores selected product IDs (not currently in use)
            selectedProductIds: [],

            // Used to filter available slots after a specific date
            afterSpecificDate: null,
        };
    },
    computed: {
        // Returns an array of product IDs from the selected booking products.
        productIds() {
            if (!this.booking || !this.booking.booking_products) return [];
            return this.booking.booking_products.map(bp => bp.product?.id).filter(id => id !== undefined);
        },

        // Checks if all days are selected
        isAllDaysSelected() {
            return this.booking.preferredDays.length === this.daysOfWeek.length;
        },

        // Checks if all time slots are selected
        isAllTimesSelected() {
            return this.booking.preferredTimes.length === this.timesOfDay.length;
        },

        // Retrieves editable calendars from Vuex
        calendars() {
            return this.$store.getters['calendars/getCalendars']().filter(calendar => calendar.can_edit);
        },

        // Retrieves all client vouchers that match the available products.
        available_client_vouchers() {
            let vouchers = this.chosen_client?.vouchers || null;

            return vouchers
                ? vouchers.filter(voucher => findIndex(this.available_products, { id: voucher.product_id }) !== -1)
                : [];
        },

        // Retrieves all products from Vuex
        products() {
            return this.$store.getters['products/products'];
        },

        // Retrieves practitioners from Vuex
        practitioners() {
            return this.$store.getters['practitioners/getPractitioners'];
        },

        // Retrieves ordered product groups from Vuex for structured display
        product_groups() {
            return this.$store.getters['products/getOrderedProductGroups']();
        },

        // Checks if the AddonServices feature is enabled
        is_addon_services_enabled() {
            return this.$store.getters['apps/isAppEnabled'](Apps.AddonServices);
        },

        // Determines if the booking is in edit mode
        is_edit_state() {
            return this.booking.id > 0;
        },

        // Returns the selected calendar object, or an empty object if none is selected
        chosen_calendar() {
            return this.booking.calendarid
                ? this.$store.getters['calendars/getCalendarById'](this.booking.calendarid)
                : {};
        },

        // Retrieves the selected payment guarantee, if any
        selectedPaymentGuarantee() {
            return this.booking.behandler_booking_payment_guarantee_id
                ? this.available_payment_guarantees.find(
                      p => p.id === this.booking.behandler_booking_payment_guarantee_id,
                  )
                : null;
        },

        // Retrieves the therapist linked to the selected payment guarantee (if applicable)
        selectedBehandlerBookingTherapist() {
            return this.selectedPaymentGuarantee
                ? this.booking.practitioners.length > 0
                    ? this.booking.practitioners[0].therapist || null
                    : this.owner.therapist || null
                : null;
        },

        /**
         * Groups available products by category.
         * If a product is available in multiple calendars, it must match the chosen calendar ID.
         */
        available_product_groups() {
            let available_product_groups = [];
            const all_product_groups = cloneDeep(this.product_groups);

            // Add a default group for ungrouped products
            all_product_groups.push({
                name: trans('online_booking.ungrouped'),
                order: 9999999,
                id: 0,
            });

            // Initialize each group with an empty product list
            forEach(all_product_groups, group => {
                group.products = [];
                available_product_groups.push(group);

                // Assign products to their respective groups
                forEach(this.available_products, product => {
                    if (product.product_group_id === group.id) {
                        group.products.push(product);
                    }
                });
            });

            return sortBy(sortBy(available_product_groups, 'name'), 'order');
        },

        // Filters available products based on calendar selection
        available_products() {
            let available_products = [];
            let all_products = cloneDeep(this.products);

            if (this.selectedPaymentGuarantee) {
                all_products = all_products.filter(product =>
                    this.selectedBehandlerBookingTherapist?.treatments.some(
                        treatment => treatment.product.id === product.id,
                    ),
                );
            }

            forEach(all_products, product => {
                if (!this.booking.calendarid) {
                    const selectedPractitioner = this.practitioners.find(p => p.id === this.booking.practitionerid);

                    if (!selectedPractitioner?.calendar_permissions) {
                        available_products.push(product);
                        return;
                    }

                    const allowedCalendarIds = selectedPractitioner.calendar_permissions
                        .filter(p => p.level.value > 0)
                        .map(p => p.calendar_id);

                    const hasPermission = product.calendars.some(c => allowedCalendarIds.includes(c.id));
                    if (hasPermission) {
                        available_products.push(product);
                    }
                } else {
                    forEach(product.calendars, calendar => {
                        if (calendar.id === this.booking.calendarid) {
                            available_products.push(product);
                        }
                    });
                }
            });

            return sortBy(sortBy(available_products, 'name'), 'order');
        },

        /**
         * Returns a filtered list of calendars based on:
         * 1. The practitioner's access permissions.
         * 2. Whether the calendar supports all selected products.
         */
        filteredCalendars() {
            let allowedCalendars = this.calendars.filter(calendar => calendar.available_online_book);

            // Find the selected practitioner
            const selectedPractitioner = this.practitioners.find(p => p.id === this.booking.practitionerid);

            // Filter by practitioner's allowed calendars
            if (selectedPractitioner?.calendar_permissions) {
                const allowedCalendarIds = selectedPractitioner.calendar_permissions
                    .filter(p => p.level.value > 0)
                    .map(p => p.calendar_id);

                allowedCalendars = allowedCalendars.filter(calendar => allowedCalendarIds.includes(calendar.id));
            }

            // Ensure the calendar supports all selected products
            const selectedProductIds = this.productIds;
            if (selectedProductIds.length > 0) {
                allowedCalendars = allowedCalendars.filter(calendar =>
                    selectedProductIds.every(prodId => calendar.products.some(p => p.id === prodId)),
                );
            }

            return allowedCalendars;
        },

        ...mapStores(useCalendarSidebarStore),
    },
    watch: {
        /**
         * When the calendar changes:
         * - Ensure the selected practitioner is still valid.
         * - If not, reset the practitioner selection.
         */
        'booking.calendarid'(newVal, oldVal) {
            if (newVal !== oldVal) {
                const selectedPractitioner = this.practitioners.find(p => p.id === this.booking.practitionerid);

                // If no valid practitioner or no permissions, reset selection
                if (!selectedPractitioner?.calendar_permissions) {
                    this.booking.practitionerid = '';
                    return;
                }

                // If calendar is cleared, do nothing
                if (!newVal) return;

                // Get allowed calendar IDs for the practitioner
                const allowedCalendarIds = selectedPractitioner.calendar_permissions
                    .filter(p => p.level.value > 0)
                    .map(p => p.calendar_id);

                // If selected calendar is not allowed, reset practitioner selection
                if (!allowedCalendarIds.includes(newVal)) {
                    this.booking.practitionerid = '';
                }
            }
        },

        /**
         * When the practitioner changes:
         * - Ensure the selected calendar is within their allowed permissions.
         * - If not, reset the calendar selection.
         */
        'booking.practitionerid'(newVal, oldVal) {
            if (newVal !== oldVal) {
                if (!newVal) return;

                const selectedPractitioner = this.practitioners.find(p => p.id === newVal);


                // If no valid practitioner or no permissions, reset calendar
                if (!selectedPractitioner?.calendar_permissions) {
                    this.booking.calendarid = '';
                    return;
                }

                // Get allowed calendar IDs for the practitioner
                const allowedCalendarIds = selectedPractitioner.calendar_permissions
                    .filter(p => p.level.value > 0)
                    .map(p => p.calendar_id);

                // If current calendar is not allowed, reset selection
                if (!allowedCalendarIds.includes(this.booking.calendarid)) {
                    this.booking.calendarid = '';
                }
            }
        },

        /**
         * When selected products change:
         * - If no products remain, do nothing.
         * - Ensure the selected calendar supports all chosen products.
         * - If not, reset the calendar selection.
         */
        'booking.booking_products'(newVal, oldVal) {
            if (newVal !== oldVal) {
                if (!newVal?.length || newVal[0].id === null) return; // No valid products selected

                const selectedCalendar = this.calendars.find(c => c.id === this.booking.calendarid);

                // If selected calendar is invalid, reset it
                if (!selectedCalendar) {
                    this.booking.calendarid = '';
                    return;
                }

                // Get list of products available in the selected calendar
                const availableProductIds = selectedCalendar.products.map(p => p.id);

                // Check if all selected products exist in this calendar
                const allProductsExist = newVal.every(product => availableProductIds.includes(product.id));

                // Reset calendar if any product is unavailable
                if (!allProductsExist) {
                    this.booking.calendarid = '';
                }
            }
        },

        /**
         * When the modal is closed, trigger cleanup actions.
         */
        showModal() {
            if (!this.showModal) {
                this.$nextTick(() => {
                    this.cancel();
                });
            }
        },

        /**
         * When copiedBooking is updated:
         * - Set default practitioner if none is selected.
         * - Copy booking products while ensuring addons can be updated.
         * - Sync client data.
         */
        copiedBooking: {
            immediate: true, // Run on component mount
            handler(newVal) {
                if (newVal?.practitioners?.length > 0 && !this.booking.practitionerid) {
                    this.booking.practitionerid = newVal.practitioners[0].id;
                }

                if (newVal?.booking_products?.length > 0) {
                    this.booking.booking_products = newVal.booking_products.map(bp => ({
                        ...bp,
                        can_update: true,
                        addons: bp.addons.map(addon => ({
                            ...addon,
                            can_update: true,
                        })),
                    }));
                }

                this.booking.clientid = newVal.clientid;
                this.booking.client = newVal.client;
            },
        },
    },

    mounted() {
        this.showModal = true;

        if (this.copiedBooking.calendarid) {
            this.booking.calendarid = this.copiedBooking.calendarid;
        }

        // Initialize all checkboxes as selected by default
        this.toggleAllDays();
        this.toggleAllTimes();
    },

    methods: {
        /**
         * Toggles an array property between "all selected" and "none selected."
         * @param {String} propName - The booking property to toggle (e.g., 'preferredDays' or 'preferredTimes').
         * @param {Array} allValues - The full list of possible values.
         */
        toggleAll(propName, allValues) {
            this.booking[propName] = this.booking[propName].length === allValues.length ? [] : [...allValues];
        },

        /**
         * Toggles all days of the week on/off.
         */
        toggleAllDays() {
            const allCodes = this.daysOfWeek.map(day => day.code);
            this.toggleAll('preferredDays', this.booking.preferredDays.length === allCodes.length ? [] : allCodes);
        },

        /**
         * Toggles all times of the day on/off.
         */
        toggleAllTimes() {
            const allCodes = this.timesOfDay.map(time => time.code);
            this.toggleAll('preferredTimes', this.booking.preferredTimes.length === allCodes.length ? [] : allCodes
            );
        },

        /**
         * Emits a cancel event and closes the modal.
         */
        cancel() {
            this.$emit('cancel');
        },

        /**
         * Handles the selection of a suggested appointment slot.
         * @param {Object} slot - The selected time slot.
         * @param {String} slot.start - Start time of the appointment.
         * @param {String} slot.end - End time of the appointment.
         * @param {String} slot.calendarId - The selected calendar ID.
         */
        handleSlotSelection({ start, end, calendarId }) {
            const selectedPractitioner = this.practitioners.find(p => p.id === this.booking.practitionerid);
            
            this.calendarSidebarStore.navigateTo(SidebarViews.CREATE_CLIENT_BOOKING, {
                start,
                end,
                calendarId,
                selectProducts: this.booking.booking_products ?? [],
                preferredDays: this.booking.preferredDays ?? [],
                preferredTimes: this.booking.preferredTimes ?? [],
                practitioner: selectedPractitioner ?? null,
                clientid: this.booking.clientid || null,
                mode: 'quick',
            });

            this.showModal = false;
        },
    },
};
</script>
